/**
 * src/utils/roleImplications.js
 *
 * Identity module — what access a job role IMPLIES.
 *
 * A role definition (`state.roleDefinitions[]`) may carry an `implies` block:
 *
 *   implies: {
 *     platformRoleId:     string | null,   // an id from state.seasuiteRoles
 *     governanceRoleKeys: string[]         // roleKeys from governance-roles-crud
 *   }
 *
 * This is the "Plan" stage of the identity module's PDCA loop — the one place
 * an administrator states what access the ISMS requires for a role, rather
 * than it being inferred from what people happen to hold.
 *
 * BACKWARD COMPATIBILITY IS THE POINT OF THIS FILE. Every role definition in
 * production today predates `implies`. An absent, null, or malformed block
 * means "this role implies nothing" — never a crash, and never a default that
 * silently implies something. Read the block through `readImplication()`
 * everywhere rather than reaching into `role.implies` at each call site, so
 * that rule lives in exactly one place.
 *
 * IDN-B-001 — an implication states what access the role REQUIRES. It says
 * nothing about whether its holder is competent to exercise it.
 */

/** The value every role without a usable `implies` block reads as. Frozen so a
 *  caller cannot accidentally mutate the shared empty case. */
export const NO_IMPLICATION = Object.freeze({
  platformRoleId: null,
  governanceRoleKeys: Object.freeze([]),
});

function cleanKeys(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  for (const k of value) {
    if (typeof k !== 'string') continue;
    const key = k.trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * Read a role definition's implication, tolerating every legacy and malformed
 * shape. Always returns a usable object — never null, never throws.
 *
 * @param {object|null|undefined} roleDef
 * @returns {{ platformRoleId: string|null, governanceRoleKeys: string[] }}
 */
export function readImplication(roleDef) {
  const raw = roleDef?.implies;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return NO_IMPLICATION;

  const platformRoleId =
    typeof raw.platformRoleId === 'string' && raw.platformRoleId.trim()
      ? raw.platformRoleId.trim()
      : null;
  const governanceRoleKeys = cleanKeys(raw.governanceRoleKeys);

  if (!platformRoleId && governanceRoleKeys.length === 0) return NO_IMPLICATION;
  return { platformRoleId, governanceRoleKeys };
}

/** True when the role states any access requirement at all. */
export function hasImplication(roleDef) {
  const imp = readImplication(roleDef);
  return imp.platformRoleId !== null || imp.governanceRoleKeys.length > 0;
}

/**
 * Build the value to persist on a role definition. Returns `undefined` for an
 * empty implication so a role that requires nothing carries no key at all —
 * which keeps a legacy record byte-identical when it is edited for some other
 * reason, and keeps "requires nothing" expressed exactly one way.
 *
 * @returns {{platformRoleId: string|null, governanceRoleKeys: string[]}|undefined}
 */
export function normaliseImplication({ platformRoleId, governanceRoleKeys } = {}) {
  const imp = readImplication({ implies: { platformRoleId, governanceRoleKeys } });
  if (imp === NO_IMPLICATION) return undefined;
  return { platformRoleId: imp.platformRoleId, governanceRoleKeys: [...imp.governanceRoleKeys] };
}

/**
 * Resolve an implication against the live catalogues, separating what resolves
 * from what does not.
 *
 * A platform role or governance role named by an implication can disappear —
 * deleted on its own tab, or a governance key removed from the catalogue. That
 * is a real gap in the ISMS's stated requirements, so it is returned as
 * `missing*` rather than filtered out (IDN-B-004: never hide what you could
 * not resolve).
 *
 * `governanceKnown: false` means the governance catalogue was not available —
 * flag-disabled or a failed read. Governance keys are then returned as
 * `unresolvedGovernanceKeys`, which is NOT the same as missing: unknown is
 * never reported as absent.
 *
 * @param {object} roleDef
 * @param {object} catalogues
 * @param {Array}  catalogues.seasuiteRoles   state.seasuiteRoles
 * @param {Array}  catalogues.governanceDefs  governance role definitions
 * @param {boolean} catalogues.governanceKnown whether governanceDefs is trustworthy
 */
export function describeImplication(
  roleDef,
  { seasuiteRoles = [], governanceDefs = [], governanceKnown = false } = {}
) {
  const imp = readImplication(roleDef);

  const platformRole = imp.platformRoleId
    ? (seasuiteRoles || []).find((r) => r.id === imp.platformRoleId) || null
    : null;
  const missingPlatformRoleId = imp.platformRoleId && !platformRole ? imp.platformRoleId : null;

  const byKey = new Map((governanceDefs || []).map((d) => [d.roleKey, d]));
  const governanceRoles = [];
  const missingGovernanceKeys = [];
  const unresolvedGovernanceKeys = [];

  for (const key of imp.governanceRoleKeys) {
    if (!governanceKnown) {
      unresolvedGovernanceKeys.push(key);
      continue;
    }
    const def = byKey.get(key);
    if (def) governanceRoles.push({ roleKey: key, name: def.displayName || key });
    else missingGovernanceKeys.push(key);
  }

  return {
    platformRoleId: imp.platformRoleId,
    platformRole: platformRole ? { id: platformRole.id, name: platformRole.name } : null,
    missingPlatformRoleId,
    governanceRoles,
    missingGovernanceKeys,
    unresolvedGovernanceKeys,
    governanceKnown,
    isEmpty: !hasImplication(roleDef),
  };
}
